// Capture-time geotagging for job photos (corner-photo mapping).
//
// The location must reflect where the photo was TAKEN, not where it's uploaded from.
// A crew may shoot corners in the field and upload later from the office — so we only
// attach the device's current location when the photo was *freshly captured*. An old
// gallery photo gets no device coords (the server still reads EXIF GPS as the source
// of truth when present).

const FRESH_MS = 5 * 60 * 1000; // treat a photo modified within 5 min as a live capture

/**
 * Resolve the device's current location for a freshly-captured photo file.
 * Returns { lat, lng, accuracy } or null (old photo, no permission, no fix, or no API).
 * Never rejects — geotagging is best-effort and must not block uploads.
 */
export function getDeviceLocationForFile(file) {
  return new Promise((resolve) => {
    if (!file || !('geolocation' in navigator)) return resolve(null);
    const age = Date.now() - (file.lastModified || 0);
    if (age > FRESH_MS) return resolve(null); // not a live capture — don't trust upload location
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}

/** Append device-location fields to a FormData if a fresh fix is available. */
export async function appendDeviceLocation(formData, file) {
  const loc = await getDeviceLocationForFile(file);
  if (loc) {
    formData.append('lat', loc.lat);
    formData.append('lng', loc.lng);
  }
  return loc;
}
